import type {
  CustomTransfersParams,
  LayoutCard,
  RangeValue
} from './custom-transfers-slice';
import type { PlayerPosition } from '@api-service/players';

type CustomTransfersLocalState = {
  layoutOrder: LayoutCard[];
  noOfTransfers: RangeValue;
  effectiveOwnership: RangeValue;
  playerPrice: RangeValue;
  playerPosition: PlayerPosition | null;
  teamIds: string[];
  playerInItems: { value: string }[];
  playerOutIds: string[];
};

/**
 * The floor a filtered request runs under when the member has not set one.
 *
 * The API defaults it to zero, which keeps "make no transfer" in the solver's
 * feasible set so that an already optimal squad is left alone. That is right for
 * the recommended tab and wrong here: a member who has filtered to goalkeepers
 * is asking which goalkeeper to buy, not whether to buy one, and answering
 * "none" whenever the squad beats every goalkeeper the filter allows reads as
 * the filter matching nobody.
 */
const MINIMUM_FILTERED_TRANSFERS = 1;

export const buildCustomTransfersParams = (
  localState: CustomTransfersLocalState
): CustomTransfersParams => {
  const params: CustomTransfersParams = {};

  for (const card of localState.layoutOrder) {
    switch (card) {
      case 'noOfTransfers':
        params.minTransfers = Math.min(
          localState.noOfTransfers.min,
          localState.noOfTransfers.max
        );
        params.maxTransfers = Math.max(
          localState.noOfTransfers.min,
          localState.noOfTransfers.max
        );
        break;
      case 'playerPosition':
        if (localState.playerPosition) {
          params.position = localState.playerPosition;
        }
        break;
      case 'team':
        if (localState.teamIds.length) {
          params.teamIds = localState.teamIds.join(',');
        }
        break;
      case 'effectiveOwnership':
        params.minEffectiveOwnership = Math.min(
          localState.effectiveOwnership.min,
          localState.effectiveOwnership.max
        );
        params.maxEffectiveOwnership = Math.max(
          localState.effectiveOwnership.min,
          localState.effectiveOwnership.max
        );
        break;
      case 'playerPrice':
        params.minPrice = Math.min(
          localState.playerPrice.min,
          localState.playerPrice.max
        );
        params.maxPrice = Math.max(
          localState.playerPrice.min,
          localState.playerPrice.max
        );
        break;
      case 'playerIn':
        if (localState.playerInItems.length) {
          params.playerIdsIn = localState.playerInItems
            .map((item) => item.value)
            .join(',');
        }
        break;
      case 'playerOut':
        if (localState.playerOutIds.length) {
          params.playerIdsOut = localState.playerOutIds.join(',');
        }
        break;
    }
  }

  // Left empty when nothing was filtered, because the suggestions query is
  // gated on that: a floor on its own would read as a request and fetch
  // recommendations for a member who has asked for nothing. The "number of
  // transfers" card sets its own floor, including a zero one, and that stands.
  const hasFilters = Object.keys(params).length > 0;
  if (hasFilters && params.minTransfers === undefined) {
    params.minTransfers = MINIMUM_FILTERED_TRANSFERS;
  }

  return params;
};
